import Phaser from 'phaser';
import { COLORS, FONTS, FORSKUDD_PER_TIER, GAME_HEIGHT, GAME_WIDTH, MODAL_BREDDE, MODAL_PADDING, TIER_COLORS, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type { Manuscript } from '@/data/types';
import type { GameStateData } from '@/state/GameState';
import { konkurrentKatalog } from '@/data/konkurrenter';
import { sfx } from '@/systems/sfx';
import { gameClock } from '@/systems/gameClock';
import { rivalen } from '@/systems/rival';
import { lestFlaggFor, manusInntrykk } from '@/systems/manusInntrykk';
import { stjerneStreng } from '@/systems/anmeldelser';
import { TIDSKOSTNADER } from '@/data/timeCosts';
import { animateModalGlide } from '@/ui/modalGlide';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { modalFokusTrap } from '@/ui/ModalFokusTrap';
import { Kolonne } from '@/ui/layout';

const SHEET_W = MODAL_BREDDE;
const CARD_WIDTH = MODAL_BREDDE - 60;
// Runde 45: bevisst avvik fra KORT_PADDING (16). Manuskript-kortene
// inneholder mye info (tittel + forfatter + sjanger + score + 2 knapper)
// og trenger 32 px margin for å føles luftige. Bruker MODAL_PADDING-
// konstanten for traceability.
const CARD_PADDING = MODAL_PADDING;
const CARD_GAP = 28;

export class InboxModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private confirmLayer?: Phaser.GameObjects.Container;
  // Scroll-tilstand (fiks etter spilltest): 4+ manus sprengte arket.
  private scrollY = 0;
  private scrollMax = 0;
  private sisteDragTs = 0;
  private dragStartY = 0;
  private dragStartScroll = 0;
  private dragAktiv = false;
  private førsteKortBounds: { x: number; y: number; w: number; h: number } | null = null;
  private førsteTilbyKnappBounds: { x: number; y: number; w: number; h: number } | null = null;

  constructor(private scene: Phaser.Scene) {}

  /**
   * Returnerer en bounding-rect for første «Tilby kontrakt»-knapp, dersom
   * innboksen er åpen og det finnes minst ett manus uten konkurrent-by.
   */
  førsteTilbyRect(): { x: number; y: number; w: number; h: number } | null {
    if (!this.layer) return null;
    return this.førsteTilbyKnappBounds;
  }

  førsteKortRect(): { x: number; y: number; w: number; h: number } | null {
    if (!this.layer) return null;
    return this.førsteKortBounds;
  }

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(100);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.scene.input.on('pointerdown', this.onPointerDown, this);
    this.scene.input.on('pointermove', this.onPointerMove, this);
    this.scene.input.on('pointerup', this.onPointerUp, this);
    this.render();
    animateModalGlide(this.scene, this.layer, { ariaTittel: 'Innboks' });
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    if (!this.layer) return;
    this.scene.input.off('wheel', this.onWheel, this);
    this.scene.input.off('pointerdown', this.onPointerDown, this);
    this.scene.input.off('pointermove', this.onPointerMove, this);
    this.scene.input.off('pointerup', this.onPointerUp, this);
    this.scrollY = 0;
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.closeConfirm();
    this.layer?.destroy(true);
    this.layer = undefined;
    modalFokusTrap.lukk();
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;

    const sheetMarginX = (GAME_WIDTH - SHEET_W) / 2;
    const sheet = lagRundetRekt(
      this.scene,
      sheetMarginX,
      96,
      SHEET_W,
      GAME_HEIGHT - 192,
      COLORS.beige,
      0.98,
      14,
    ).setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const headerY = 132;
    // Layout-primitiv (2026-06-04): header og undertittel stables fra målt
    // høyde via Kolonne. Erstatter hardkodet +92 og +180 som antok 1-linjes
    // tittel; «Innboks — Dag N» kan wrappe på smale skjermer.
    const header = this.scene.add
      .text(sheetMarginX + CARD_PADDING, 0, `Innboks — Dag ${snap.dag}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(54),
        color: '#862736',
        wordWrap: { width: SHEET_W - CARD_PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);

    const subtitle = this.scene.add
      .text(
        sheetMarginX + CARD_PADDING,
        0,
        snap.innboks.length === 1
          ? '1 nytt manuskript venter'
          : `${snap.innboks.length} nye manuskripter venter`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(24),
          color: '#3a2e22',
        },
      )
      .setOrigin(0, 0);

    const topp = new Kolonne(sheetMarginX + CARD_PADDING, headerY, 14);
    topp.legg(header);
    topp.legg(subtitle);
    this.layer.add([header, subtitle]);

    const closeBtn = this.scene.add
      .text(sheetMarginX + SHEET_W - CARD_PADDING, headerY - 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const listX = sheetMarginX + (SHEET_W - CARD_WIDTH) / 2;
    const listTop = topp.bunn() + 28;
    const listBunn = 96 + (GAME_HEIGHT - 192) - 76;
    const listHoyde = listBunn - listTop;

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);
    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 6, listTop, CARD_WIDTH + 12, listHoyde);
    listContainer.setMask(maske.createGeometryMask());

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    this.førsteKortBounds = null;
    this.førsteTilbyKnappBounds = null;
    if (snap.innboks.length === 0) {
      const emptyBox = lagRundetRekt(
        this.scene,
        listX,
        listTop,
        CARD_WIDTH,
        220,
        COLORS.white,
        0.9,
        8,
      ).setStrokeStyle(2, COLORS.oakDark, 0.4);
      const emptyText = this.scene.add
        .text(
          listX + CARD_WIDTH / 2,
          listTop + 110,
          'Innboksen er tom.\nKlikk Neste dag for nye manus.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(28),
            color: '#1a1a1a',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      this.layer.add([emptyBox, emptyText]);
    } else {
      let førsteRegistrert = false;
      for (const manus of snap.innboks) {
        const { container: card, hoyde } = this.buildCard(listX, y, manus, snap);
        listContainer.add(card);
        if (!førsteRegistrert) {
          this.førsteKortBounds = {
            x: listX,
            y,
            w: CARD_WIDTH,
            h: hoyde,
          };
          // Tilby/Match-knappen er nederst til høyre i kortet
          if (manus.konkurrentBy) {
            const buttonsY = y + hoyde - 92;
            const halvBredde = (CARD_WIDTH - CARD_PADDING * 2 - 16) / 2;
            this.førsteTilbyKnappBounds = {
              x: listX + CARD_PADDING,
              y: buttonsY,
              w: halvBredde,
              h: 72,
            };
          } else {
            const buttonsY = y + hoyde - 92;
            const buttonWidth = (CARD_WIDTH - CARD_PADDING * 2 - 32) / 3;
            const knappX = listX + CARD_PADDING + (buttonWidth + 16) * 2;
            this.førsteTilbyKnappBounds = {
              x: knappX,
              y: buttonsY,
              w: buttonWidth,
              h: 72,
            };
          }
          førsteRegistrert = true;
        }
        y += hoyde + CARD_GAP;
        bygdHoyde += hoyde + CARD_GAP;
      }
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;
    if (this.scrollMax > 0) {
      const igjen = Math.round(this.scrollMax - this.scrollY);
      const indikator = this.scene.add
        .text(
          sheetMarginX + SHEET_W / 2,
          listBunn + 4,
          igjen > 4 ? '⌄ dra eller rull for flere manus ⌄' : 'Slutten av bunken',
          {
            fontFamily: FONTS.hud,
            fontSize: fontStr(16),
            color: '#8b5e3c',
            fontStyle: '700',
          },
        )
        .setOrigin(0.5, 0);
      this.layer.add(indikator);
    }

    const footer = this.scene.add
      .text(
        sheetMarginX + SHEET_W / 2,
        96 + (GAME_HEIGHT - 192) - 56,
        'Forskudd: D=20k · C=40k · B=80k · A=140k · S=220k',
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#3a2e22',
        },
      )
      .setOrigin(0.5, 0.5);
    this.layer.add(footer);
  }

  private buildCard(
    x: number,
    y: number,
    manus: Manuscript,
    snap: GameStateData,
  ): { container: Phaser.GameObjects.Container; hoyde: number } {
    const container = this.scene.add.container(x, y);
    const harBy = !!manus.konkurrentBy;
    const fraForfatter = manus.fraForfatterId
      ? snap.authors[manus.fraForfatterId]
      : undefined;
    const harForfatterBanner = !!fraForfatter;

    // Layout-primitiv (2026-06-04): innholdet (bannere → tittel → forfatter →
    // badges → blurb) stables fra målt høyde via Kolonne. Korthøyden utledes
    // av faktisk innhold pluss knappe-blokken — erstatter de hardkodede 660/
    // 520(+90)-tallene og nestY-spranget +56/+84 som overlappet når tittel
    // eller blurb wrappet til flere linjer enn antatt.
    const innhold = new Kolonne(CARD_PADDING, 28, 16);
    const tegnEtter: Phaser.GameObjects.GameObject[] = [];

    if (harForfatterBanner && fraForfatter) {
      const banner = this.buildForfatterBanner(
        CARD_PADDING,
        0,
        CARD_WIDTH - CARD_PADDING * 2,
        fraForfatter.navn,
      );
      innhold.legg(banner.container);
      tegnEtter.push(banner.container);
    }

    if (harBy && manus.konkurrentBy) {
      const banner = this.buildKonkurrentBanner(
        CARD_PADDING,
        0,
        CARD_WIDTH - CARD_PADDING * 2,
        manus.konkurrentBy.konkurrentId,
        manus.konkurrentBy.beloep,
      );
      innhold.legg(banner.container);
      tegnEtter.push(banner.container);
    }

    const title = this.scene.add
      .text(CARD_PADDING, 0, manus.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(40),
        color: '#1a1a1a',
        wordWrap: { width: CARD_WIDTH - CARD_PADDING * 2 },
      })
      .setOrigin(0, 0);
    innhold.legg(title);
    tegnEtter.push(title);

    const erPseudonym = !manus.author || manus.author.trim().toLowerCase() === 'ukjent forfatter';
    const navnVis = erPseudonym ? 'Pseudonym (foreløpig ukjent)' : manus.author;
    const author = this.scene.add
      .text(CARD_PADDING, 0, navnVis, {
        fontFamily: FONTS.body,
        fontSize: fontStr(26),
        color: '#3a2e22',
        fontStyle: erPseudonym ? 'italic' : 'normal',
      })
      .setOrigin(0, 0);
    innhold.legg(author, 14);
    tegnEtter.push(author);

    const badges = this.buildBadges(CARD_PADDING, 0, manus);
    // Badge-raden har origin (0,0) men måler ikke .height selv; bygg gir en
    // container med faste 0-størrelse children. Vi reserverer kjent badge-
    // høyde (tekst + 16 px padding) eksplisitt.
    innhold.legg(badgeStable(badges, 52), 14);
    tegnEtter.push(badges);

    const blurb = this.scene.add
      .text(CARD_PADDING, 0, `«${manus.editorial_blurb}»`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        fontStyle: 'italic',
        wordWrap: { width: CARD_WIDTH - CARD_PADDING * 2 },
      })
      .setOrigin(0, 0);
    innhold.legg(blurb, 14);
    tegnEtter.push(blurb);

    // «Les et utdrag»-laget (spilltest-funn: manusvalget var blindt).
    // Ulest: slank knapp som koster 60 min. Lest: magefølelsen vises.
    const erLest = gameState.harFlag(lestFlaggFor(manus.isbn));
    if (erLest) {
      const inntrykk = manusInntrykk(manus, snap.dag);
      const magefolelse = this.scene.add
        .text(
          CARD_PADDING,
          0,
          `Magefølelse: ${stjerneStreng(inntrykk.terning)}
${inntrykk.notat}
${inntrykk.sesongLinje}`,
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(21),
            color: '#3a2410',
            wordWrap: { width: CARD_WIDTH - CARD_PADDING * 2 - 20 },
            lineSpacing: 5,
            backgroundColor: 'rgba(244,213,141,0.35)',
            padding: { left: 10, right: 10, top: 8, bottom: 8 },
          },
        )
        .setOrigin(0, 0);
      innhold.legg(magefolelse, 14);
      tegnEtter.push(magefolelse);
    } else {
      const lesKnapp = this.makeButton(
        CARD_PADDING,
        innhold.bunn() + 14,
        CARD_WIDTH - CARD_PADDING * 2,
        'Les et utdrag — 60 min',
        'secondary',
        () => {
          gameState.settFlag(lestFlaggFor(manus.isbn));
          gameClock.brukTid(TIDSKOSTNADER.lesManus, `leste «${manus.title}»`);
          this.render();
        },
      );
      innhold.hoppOver(14 + 72);
      tegnEtter.push(lesKnapp);
    }

    // Knappe-blokk: med-by har to rader (78 + 14 + 78), uten-by én rad (72).
    // Bunn-margin under nederste knapp holdes på 20 px (som før).
    const knappeBlokkH = harBy ? 78 + 14 + 78 : 72;
    const cardHeight = innhold.bunn() + 28 + knappeBlokkH + 20;
    const buttonsY = cardHeight - 92;

    const bg = lagRundetRekt(
      this.scene,
      0,
      0,
      CARD_WIDTH,
      cardHeight,
      COLORS.white,
      1,
      8,
    ).setStrokeStyle(2, COLORS.oakDark, 0.5);
    // Bakgrunn bakerst, så stables innhold og knapper oppå.
    container.add(bg);
    container.add(tegnEtter);

    if (harBy && manus.konkurrentBy) {
      this.buildKnapperMedBy(container, buttonsY, manus, snap);
    } else {
      this.buildKnapperUtenBy(container, buttonsY, manus, snap);
    }

    // 5C-fiks etter spilltest: rival-budet var usynlig i innboksen.
    // Marineblått frist-merke øverst til høyre når KD byr på manuset.
    const bud = rivalen.budet;
    if (bud && bud.isbn === manus.isbn) {
      const merke = this.scene.add
        .text(
          CARD_WIDTH - 10,
          -12,
          `KAPELLE DAMM BYR — ${Math.max(0, bud.fristDag - snap.dag)} dager igjen`,
          {
          fontFamily: FONTS.hud,
          fontSize: fontStr(15),
          color: '#f4ead5',
          fontStyle: '700',
          backgroundColor: 'rgba(31,45,68,0.97)',
          padding: { left: 12, right: 12, top: 6, bottom: 6 },
        },
        )
        .setOrigin(1, 0.5)
        .setAngle(1.5);
      container.add(merke);
    }

    return { container, hoyde: cardHeight };
  }

  private buildKnapperUtenBy(
    container: Phaser.GameObjects.Container,
    buttonsY: number,
    manus: Manuscript,
    snap: GameStateData,
  ): void {
    const buttonWidth = (CARD_WIDTH - CARD_PADDING * 2 - 32) / 3;

    const avslaaBtn = this.makeButton(
      CARD_PADDING,
      buttonsY,
      buttonWidth,
      'Avslå · 30 min',
      'outline',
      () => this.medVurderingstid(() => gameState.avslaaManus(manus.isbn)),
    );
    container.add(avslaaBtn);

    const tilbakeBtn = this.makeButton(
      CARD_PADDING + buttonWidth + 16,
      buttonsY,
      buttonWidth,
      'Send tilbake · 30 min',
      'secondary',
      () => this.medVurderingstid(() => gameState.sendTilbake(manus.isbn)),
    );
    container.add(tilbakeBtn);

    const forskudd = FORSKUDD_PER_TIER[manus.tier];
    const harRaad = snap.kroner >= forskudd;
    const tilbyLabel = harRaad
      ? `Tilby (${formaterKr(forskudd)})`
      : 'For lite kapital';
    const tilbyBtn = this.makeButton(
      CARD_PADDING + (buttonWidth + 16) * 2,
      buttonsY,
      buttonWidth,
      tilbyLabel,
      'primary',
      () => this.openConfirm(manus),
      !harRaad,
    );
    container.add(tilbyBtn);
  }

  /** Mad TV: hver manus-beslutning koster vurderingstid (data/timeCosts.ts). */
  private medVurderingstid(handling: () => void): void {
    handling();
    gameClock.brukTid(TIDSKOSTNADER.manusVurdering, 'vurderte et manus');
  }

  private buildKnapperMedBy(
    container: Phaser.GameObjects.Container,
    buttonsY: number,
    manus: Manuscript,
    snap: GameStateData,
  ): void {
    const by = manus.konkurrentBy!;
    const konkurrent = konkurrentKatalog.findById(by.konkurrentId);
    const navn = konkurrent?.spillNavn ?? 'Konkurrent';
    const matchPris = by.beloep + 1;

    // 4 knapper i 2 rader
    const radH = 78;
    const radGap = 14;
    const halvBredde = (CARD_WIDTH - CARD_PADDING * 2 - 16) / 2;

    // Rad 1: Avslå · Send tilbake
    const avslaaBtn = this.makeButton(
      CARD_PADDING,
      buttonsY - radH - radGap,
      halvBredde,
      'Avslå',
      'outline',
      () => this.medVurderingstid(() => gameState.avslaaManus(manus.isbn)),
    );
    container.add(avslaaBtn);

    const tilbakeBtn = this.makeButton(
      CARD_PADDING + halvBredde + 16,
      buttonsY - radH - radGap,
      halvBredde,
      'Send tilbake',
      'secondary',
      () => this.medVurderingstid(() => gameState.sendTilbake(manus.isbn)),
    );
    container.add(tilbakeBtn);

    // Rad 2: Match og tilby · La X ta den
    const harRaad = snap.kroner >= matchPris;
    const matchLabel = harRaad
      ? `Match og tilby (${formaterKr(matchPris)})`
      : 'For lite kapital til match';
    const matchBtn = this.makeButton(
      CARD_PADDING,
      buttonsY,
      halvBredde,
      matchLabel,
      'primary',
      () => this.openConfirmMatch(manus, matchPris, navn),
      !harRaad,
    );
    container.add(matchBtn);

    const laGaaBtn = this.makeButton(
      CARD_PADDING + halvBredde + 16,
      buttonsY,
      halvBredde,
      `La ${navn} ta den (−2 omdømme)`,
      'secondary',
      () => this.medVurderingstid(() => gameState.avslaaKonkurrentBy(manus.isbn)),
    );
    container.add(laGaaBtn);
  }

  private buildForfatterBanner(
    x: number,
    y: number,
    width: number,
    forfatterNavn: string,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const padX = 16;
    const padY = 12;

    const prefix = this.scene.add
      .text(padX, padY, '✨ Nytt manus fra din forfatter ', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#3a2410',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    const navn = this.scene.add
      .text(padX + prefix.width, padY, forfatterNavn, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#3a2410',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    const h = Math.max(prefix.height, navn.height) + padY * 2;
    const bg = this.scene.add
      .rectangle(0, 0, width, h, COLORS.yellowWarm, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    c.add([bg, prefix, navn]);
    // En Phaser-Container utleder ikke .height fra barna; sett den eksplisitt
    // så Kolonne stabler tittelen UNDER banneret (ikke oppå).
    c.setSize(width, h);
    return { container: c, height: h };
  }

  private buildKonkurrentBanner(
    x: number,
    y: number,
    width: number,
    konkurrentId: string,
    beloep: number,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const c = this.scene.add.container(x, y);
    const padX = 16;
    const padY = 12;
    const konk = konkurrentKatalog.findById(konkurrentId);
    const navn = konk?.spillNavn ?? 'Konkurrent';

    const tekst = this.scene.add
      .text(
        padX,
        padY,
        `⚠  ${navn} har bydd ${formaterKr(beloep)} for dette manuset.`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(22),
          color: '#3a2410',
          fontStyle: '700',
          wordWrap: { width: width - padX * 2 },
        },
      )
      .setOrigin(0, 0);
    const h = tekst.height + padY * 2;
    const bg = this.scene.add
      .rectangle(0, 0, width, h, COLORS.yellowWarm, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.6);
    c.add([bg, tekst]);
    // Sett eksplisitt høyde så Kolonne ikke stabler tittelen oppå banneret.
    c.setSize(width, h);
    return { container: c, height: h };
  }

  private buildBadges(x: number, y: number, manus: Manuscript): Phaser.GameObjects.Container {
    const row = this.scene.add.container(x, y);

    const genreBg = this.scene.add
      .rectangle(0, 0, 0, 0, COLORS.oakLight, 1)
      .setOrigin(0, 0);
    const genreText = this.scene.add
      .text(12, 8, manus.genre, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#3a2410',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    const gW = genreText.width + 24;
    const gH = genreText.height + 16;
    genreBg.setSize(gW, gH);
    row.add([genreBg, genreText]);

    const tierColor = TIER_COLORS[manus.tier];
    const tierBg = this.scene.add
      .rectangle(gW + 12, 0, 0, 0, tierColor, 1)
      .setOrigin(0, 0);
    const tierText = this.scene.add
      .text(gW + 12 + 12, 8, `Tier ${manus.tier}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    const tW = tierText.width + 24;
    tierBg.setSize(tW, gH);
    row.add([tierBg, tierText]);

    return row;
  }

  private onWheel = (
    _p: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };

  private onPointerDown = (p: Phaser.Input.Pointer): void => {
    if (!this.layer) return;
    this.dragStartY = p.y;
    this.dragStartScroll = this.scrollY;
    this.dragAktiv = false;
  };

  private onPointerMove = (p: Phaser.Input.Pointer): void => {
    if (!this.layer || !p.isDown || this.scrollMax <= 0) return;
    const delta = this.dragStartY - p.y;
    if (!this.dragAktiv && Math.abs(delta) < 14) return;
    this.dragAktiv = true;
    this.sisteDragTs = Date.now();
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.dragStartScroll + delta));
    this.render();
  };

  private onPointerUp = (): void => {
    if (this.dragAktiv) this.sisteDragTs = Date.now();
    this.dragAktiv = false;
  };

  private makeButton(
    x: number,
    y: number,
    width: number,
    label: string,
    variant: 'primary' | 'secondary' | 'outline',
    onClick: () => void,
    disabled = false,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const height = 72;

    let fill: number = 0xffffff;
    let textColor = '#862736';
    let stroke: number = COLORS.wine;
    let strokeWidth = 3;
    let shadowAlpha = 0;

    if (variant === 'primary') {
      fill = COLORS.wine;
      textColor = '#ffffff';
      strokeWidth = 0;
      shadowAlpha = 0.32;
    } else if (variant === 'secondary') {
      fill = COLORS.oakLight;
      textColor = '#3a2410';
      stroke = COLORS.oakDark;
      strokeWidth = 0;
      shadowAlpha = 0.18;
    } else {
      shadowAlpha = 0.12;
    }

    if (disabled) {
      fill = 0xcfc6b7;
      textColor = '#7a7060';
      strokeWidth = 0;
      shadowAlpha = 0.08;
    }

    if (shadowAlpha > 0) {
      const shadow = this.scene.add
        .rectangle(2, 4, width, height, 0x000000, shadowAlpha)
        .setOrigin(0, 0);
      container.add(shadow);
    }

    const bg = this.scene.add
      .rectangle(0, 0, width, height, fill, 1)
      .setOrigin(0, 0);
    if (strokeWidth > 0) bg.setStrokeStyle(strokeWidth, stroke, 1);
    container.add(bg);

    const txt = this.scene.add
      .text(width / 2, height / 2, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: textColor,
        fontStyle: '600',
        align: 'center',
        wordWrap: { width: width - 16 },
      })
      .setOrigin(0.5);
    container.add(txt);

    if (!disabled) {
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => {
        bg.setAlpha(0.92);
        if (variant === 'primary') {
          sfx.play('click');
        }
      });
      bg.on('pointerout', () => bg.setAlpha(1));
      bg.on('pointerup', () => {
        // Slipp etter drag-scroll skal ikke trigge knappen under fingeren.
        if (Date.now() - this.sisteDragTs < 250) return;
        onClick();
      });
    }

    return container;
  }

  private openConfirm(manus: Manuscript): void {
    this.closeConfirm();
    this.confirmLayer = this.scene.add.container(0, 0).setDepth(200);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setInteractive();
    this.confirmLayer.add(backdrop);

    const dW = 880;
    const dH = 520;
    const dX = (GAME_WIDTH - dW) / 2;
    const dY = (GAME_HEIGHT - dH) / 2;

    const sheet = lagRundetRekt(
      this.scene,
      dX,
      dY,
      dW,
      dH,
      COLORS.white,
      1,
      14,
    ).setStrokeStyle(3, COLORS.wine, 0.8);
    this.confirmLayer.add(sheet);

    const title = this.scene.add
      .text(dX + dW / 2, dY + 48, 'Tilby kontrakt?', {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
      })
      .setOrigin(0.5, 0);
    this.confirmLayer.add(title);

    const forskudd = FORSKUDD_PER_TIER[manus.tier];

    const body = this.scene.add
      .text(
        dX + dW / 2,
        dY + 156,
        `«${manus.title}»\nav ${manus.author}\n\nTier ${manus.tier} · Forskudd: ${formaterKr(forskudd)}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(26),
          color: '#1a1a1a',
          align: 'center',
          wordWrap: { width: dW - 64 },
        },
      )
      .setOrigin(0.5, 0);
    this.confirmLayer.add(body);

    const btnY = dY + dH - 120;
    const btnW = 320;
    const gap = 32;
    const totalBtnW = btnW * 2 + gap;
    const startX = dX + (dW - totalBtnW) / 2;

    const avbrytBtn = this.makeButton(startX, btnY, btnW, 'Avbryt', 'outline', () =>
      this.closeConfirm(),
    );
    this.confirmLayer.add(avbrytBtn);

    const jaBtn = this.makeButton(startX + btnW + gap, btnY, btnW, 'Ja, tilby', 'primary', () => {
      const ok = gameState.tilbyKontrakt(manus.isbn);
      this.closeConfirm();
      if (!ok) return;
      gameClock.brukTid(TIDSKOSTNADER.manusVurdering, 'tilbød kontrakt');
      sfx.play('bell');
    });
    this.confirmLayer.add(jaBtn);
  }

  private openConfirmMatch(manus: Manuscript, pris: number, konkurrentNavn: string): void {
    this.closeConfirm();
    this.confirmLayer = this.scene.add.container(0, 0).setDepth(200);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setInteractive();
    this.confirmLayer.add(backdrop);

    const dW = 880;
    const dH = 580;
    const dX = (GAME_WIDTH - dW) / 2;
    const dY = (GAME_HEIGHT - dH) / 2;

    const sheet = lagRundetRekt(
      this.scene,
      dX,
      dY,
      dW,
      dH,
      COLORS.white,
      1,
      14,
    ).setStrokeStyle(3, COLORS.wine, 0.8);
    this.confirmLayer.add(sheet);

    const title = this.scene.add
      .text(dX + dW / 2, dY + 48, 'Match og tilby?', {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
      })
      .setOrigin(0.5, 0);
    this.confirmLayer.add(title);

    const body = this.scene.add
      .text(
        dX + dW / 2,
        dY + 156,
        `«${manus.title}»\nav ${manus.author}\n\n${konkurrentNavn} har bydd ${formaterKr(
          pris - 1,
        )}.\nDu betaler ${formaterKr(pris)} — én krone over.`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(24),
          color: '#1a1a1a',
          align: 'center',
          wordWrap: { width: dW - 64 },
        },
      )
      .setOrigin(0.5, 0);
    this.confirmLayer.add(body);

    const btnY = dY + dH - 120;
    const btnW = 320;
    const gap = 32;
    const totalBtnW = btnW * 2 + gap;
    const startX = dX + (dW - totalBtnW) / 2;

    const avbrytBtn = this.makeButton(startX, btnY, btnW, 'Avbryt', 'outline', () =>
      this.closeConfirm(),
    );
    this.confirmLayer.add(avbrytBtn);

    const jaBtn = this.makeButton(
      startX + btnW + gap,
      btnY,
      btnW,
      'Ja, match',
      'primary',
      () => {
        gameState.matchKonkurrentBy(manus.isbn);
        gameClock.brukTid(TIDSKOSTNADER.manusVurdering, 'matchet konkurrent-bud');
        this.closeConfirm();
        sfx.play('bell');
      },
    );
    this.confirmLayer.add(jaBtn);
  }

  private closeConfirm(): void {
    this.confirmLayer?.destroy(true);
    this.confirmLayer = undefined;
  }
}

function formaterKr(n: number): string {
  return `${n.toLocaleString('nb-NO')} kr`;
}

// Layout-primitiv (2026-06-04): badge-raden bygges av 0-størrelse children,
// så container.height er upålitelig for stabling. Denne proxyen gir Kolonne
// en fast målt høyde og videresender y-settet til den faktiske containeren.
function badgeStable(
  container: Phaser.GameObjects.Container,
  hoyde: number,
): { y: number; height: number } {
  return {
    height: hoyde,
    get y(): number {
      return container.y;
    },
    set y(v: number) {
      container.y = v;
    },
  };
}
